/**
 * Perspective Screen Component
 * Dual-perspective challenge for trump cards
 */

import React, { useState } from 'react';
import type { Perspective } from '../../../types/question';
import styles from './PerspectiveScreen.module.css';

interface PerspectiveScreenProps {
  perspective: Perspective;
  onConfirm: (answer: 0 | 1 | 2) => void;
  onSkip: () => void;
  labels: {
    title: string;
    subtitle: string;
    sourceCheck: string;
    skip: string;
    confirm: string;
    options: [string, string, string]; // LT, BE, Neutral
    topic: string;
    source: string;
    ltTitle: string;
    beTitle: string;
    aria: {
      columns: string;
      ltColumn: string;
      beColumn: string;
      neutralNote: string;
      sourceOptions: string;
      skip: string;
      confirm: string;
    };
  };
}

export const PerspectiveScreen: React.FC<PerspectiveScreenProps> = ({
  perspective,
  onConfirm,
  onSkip,
  labels,
}) => {
  const [selectedAnswer, setSelectedAnswer] = useState<0 | 1 | 2 | null>(null);

  const handleConfirm = () => {
    if (selectedAnswer !== null) {
      onConfirm(selectedAnswer);
    }
  };

  const getButtonClass = (answer: 0 | 1 | 2): string => {
    if (selectedAnswer !== answer) return styles.sourceOptBtn;
    
    switch (answer) {
      case 0:
        return `${styles.sourceOptBtn} ${styles.selectedLt}`;
      case 1:
        return `${styles.sourceOptBtn} ${styles.selectedBe}`;
      case 2:
        return `${styles.sourceOptBtn} ${styles.selectedNeutral}`;
      default:
        return styles.sourceOptBtn;
    }
  };

  return (
    <section
      id="screen-perspective"
      className={`screen ${styles.screen} active`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="persp-title"
    >
      <div className={styles.perspectiveModal}>
        <div className={styles.perspHeader}>
          <div style={{ fontSize: '1.8rem', marginBottom: '6px' }}>⚔️ 🃏 ⚔️</div>
          <h2 id="persp-title">{labels.title}</h2>
          <p>{labels.subtitle}</p>
        </div>

        <div className={styles.topic}>{labels.topic}: {perspective.topic}</div>

        <div className={styles.perspColumns} role="group" aria-label={labels.aria.columns}>
          <div className={`${styles.perspCol} ${styles.lt}`} aria-label={labels.aria.ltColumn}>
            <div className={styles.perspColTitle}>{labels.ltTitle}</div>
            <p>{perspective.lt}</p>
            <div className={styles.perspSource}>{labels.source}: {perspective.ltSource}</div>
          </div>
          <div className={`${styles.perspCol} ${styles.be}`} aria-label={labels.aria.beColumn}>
            <div className={styles.perspColTitle}>{labels.beTitle}</div>
            <p>{perspective.be}</p>
            <div className={styles.perspSource}>{labels.source}: {perspective.beSource}</div>
          </div>
        </div>

        <div className={styles.perspNeutralBox} role="note" aria-label={labels.aria.neutralNote}>
          🎓 {perspective.neutral}
        </div>

        <div className={styles.perspSourceCheck} role="group" aria-labelledby="persp-source-label">
          <h4 id="persp-source-label">{labels.sourceCheck}</h4>
          <p className={styles.sourceQuote}>{perspective.sourceQuote}</p>
          <div className={styles.sourceOptions} role="radiogroup" aria-label={labels.aria.sourceOptions}>
            <button
              className={getButtonClass(0)}
              onClick={() => setSelectedAnswer(0)}
              aria-pressed={selectedAnswer === 0}
              role="radio"
              aria-checked={selectedAnswer === 0}
            >
              {labels.options[0]}
            </button>
            <button
              className={getButtonClass(1)}
              onClick={() => setSelectedAnswer(1)}
              aria-pressed={selectedAnswer === 1}
              role="radio"
              aria-checked={selectedAnswer === 1}
            >
              {labels.options[1]}
            </button>
            <button
              className={getButtonClass(2)}
              onClick={() => setSelectedAnswer(2)}
              aria-pressed={selectedAnswer === 2}
              role="radio"
              aria-checked={selectedAnswer === 2}
            >
              {labels.options[2]}
            </button>
          </div>
        </div>

        <div className={styles.perspFooter}>
          <button
            className={styles.btnSkip}
            onClick={onSkip}
            aria-label={labels.aria.skip}
          >
            {labels.skip}
          </button>
          <button
            className={styles.btnConfirm}
            onClick={handleConfirm}
            disabled={selectedAnswer === null}
            aria-label={labels.aria.confirm}
          >
            {labels.confirm}
          </button>
        </div>
      </div>
    </section>
  );
};

export default PerspectiveScreen;
